import { useContext, useState } from 'react';

import {
  Text,
  SimpleGrid,
  Box,
  Button,
  Flex,
  Collapse,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';

import {
  changeDwellingDiscountsSurchargesItem,
  addDwellingDiscountsSurchargesItem,
  removeDwellingDiscountsSurchargesItem,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import getRadioYesNo from 'helpers/getRadioYesNo';
import isSuperAdmin from 'helpers/isSuperAdmin/isSuperAdmin';

import { AuthContext } from 'contexts/AuthContext';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface DiscountsSurchargesProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const DiscountsSurcharges = ({ dwelling, index }: DiscountsSurchargesProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const superAdmin = isSuperAdmin(user);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('', 'gray.800');
  const shadowColor = useColorModeValue(
    '0 2px 4px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.3)',
  );

  const onDiscountSurchargeItemChange = (event: any, itemIndex: number) => {
    const { name, value } = event.target;

    dispatch(
      changeDwellingDiscountsSurchargesItem({
        dwellingIndex: index,
        itemIndex,
        name,
        value,
      }),
    );
  };

  const onRadioChange = (value: string, field: string, itemIndex: number) => {
    const booleanValue =
      value === 'true' ? true : value === 'false' ? false : null;

    dispatch(
      changeDwellingDiscountsSurchargesItem({
        dwellingIndex: index,
        itemIndex,
        name: field,
        value: booleanValue,
      }),
    );
  };

  const handleAddDiscountSurchargeItem = () => {
    if (
      !dwelling.discounts_surcharges ||
      dwelling.discounts_surcharges.length < 6
    ) {
      dispatch(addDwellingDiscountsSurchargesItem(index));
    }
  };

  const handleRemoveDiscountSurchargeItem = (itemIndex: number) => {
    dispatch(
      removeDwellingDiscountsSurchargesItem({
        dwellingIndex: index,
        itemIndex,
      }),
    );
  };

  return (
    <Box
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      bg={bgColor}
      boxShadow={shadowColor}
      overflow="hidden"
    >
      <Box
        rounded="5px"
        p="10px"
        backgroundColor="brand.400"
        color="white"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box fontWeight="medium">Discounts Surcharges</Box>
        <Button
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={() => setIsCollapsed(!isCollapsed)}
          rightIcon={
            <Icon as={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
          }
        >
          {isCollapsed ? 'Expand' : 'Collapse'}
        </Button>
      </Box>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box p="6">
          {dwelling.discounts_surcharges &&
          dwelling.discounts_surcharges.length > 0 ? (
            <>
              <Text fontWeight="medium" fontSize="md" mb="15px">
                Discount/Surcharge Items
              </Text>

              {dwelling.discounts_surcharges.map(
                (item: any, itemIndex: number) => (
                  <Box
                    key={itemIndex}
                    p="15px"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    mt={itemIndex === 0 ? '0' : '15px'}
                  >
                    <Flex justify="space-between" align="center" mb="15px">
                      <Text fontWeight="medium" fontSize="sm">
                        Discount/Surcharge Item {itemIndex + 1}
                      </Text>
                      {superAdmin && (
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() =>
                            handleRemoveDiscountSurchargeItem(itemIndex)
                          }
                        >
                          Remove
                          <Text textColor="red" display="inline" ml="5px">
                            (dev mode)
                          </Text>
                        </Button>
                      )}
                    </Flex>

                    <SimpleGrid columns={4} spacing="20px">
                      <FormInput
                        section={SECTIONS.DWELLINGS}
                        label="Description"
                        name="description"
                        value={item.description}
                        onChange={(event: any) =>
                          onDiscountSurchargeItemChange(event, itemIndex)
                        }
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.DWELLINGS}
                        label="Percentage"
                        name="percentage"
                        value={item.percentage}
                        onChange={(event: any) =>
                          onDiscountSurchargeItemChange(event, itemIndex)
                        }
                        disabled
                      />

                      <RadioInput
                        section={SECTIONS.DWELLINGS}
                        label="Has applied to premium"
                        options={[
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' },
                        ]}
                        value={getRadioYesNo(item.has_applied_to_premium)}
                        onChange={(value: string) =>
                          onRadioChange(
                            value,
                            'has_applied_to_premium',
                            itemIndex,
                          )
                        }
                        stacked
                        disabled
                      />

                      <FormInput
                        section={SECTIONS.DWELLINGS}
                        label="Estimated discount/surcharge"
                        name="estimated_discount_surcharge"
                        value={item.estimated_discount_surcharge}
                        onChange={(event: any) =>
                          onDiscountSurchargeItemChange(event, itemIndex)
                        }
                        disabled
                      />
                    </SimpleGrid>
                  </Box>
                ),
              )}
            </>
          ) : (
            <Text fontSize="md" color="gray.600" fontStyle="italic">
              No discount/surcharge items added
            </Text>
          )}

          {superAdmin && (
            <Box mt="15px">
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleAddDiscountSurchargeItem}
              >
                Add Discount/Surcharge Item (
                {dwelling.discounts_surcharges?.length || 0}/6)
                <Text textColor="red" display="inline" ml="10px">
                  (dev mode)
                </Text>
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default DiscountsSurcharges;
